import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import {
  Box,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Checkbox,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useAuth, useAlert, useListing, useLanguage } from 'hooks';
import {
  addFloorplanOrderAsync,
  fetchListingAsync,
  hideModal,
  getFloorplanOrdersAsync,
} from 'state/actions';
import moment from 'moment';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RadioButtonUncheckedOutlinedIcon from '@material-ui/icons/RadioButtonUncheckedOutlined';
import { ALERT, MODAL, ENG, COLOR, SUBSCRIPTION } from 'config/constants';
import { HvModal, HvPleaseWait, HvButton } from 'components';
import { FloorplanOrder } from 'models';
import { images } from 'assets/images';
import { isMobile } from 'utils/screen';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    '& .MuiCheckbox-root': {
      color: '#00bdf2',
    },
  },
  description: {
    fontSize: 14,
    marginBottom: 10,
  },
  cancelButton: {
    backgroundColor: 'white',
    marginRight: 20,
    color: '#00bdf2',
    border: '1px solid #00bdf2',
  },
  priceContent: {
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
    padding: 10,
    width: '45%',
    marginBottom: 10,
  },
  priceList: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 10,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    marginBottom: 10,
  },
  priceCard: {
    backgroundColor: COLOR.WHITE,
    padding: 5,
    borderRadius: 8,
    marginBottom: 5,
  },
  explanation: {
    fontSize: 14,
  },
  newOrderButton: {
    marginTop: 20,
  },
  addIcon: {
    marginRight: 5,
  },
  items: {
    overflowY: 'scroll',
    maxHeight: 500,
  },
  optionTitle: {
    fontSize: 10,
  },
  optionSubtitle: {
    fontSize: 10,
  },
  summary: {
    fontSize: 12,
  },
  accordion: {
    backgroundColor: '#f0f0f0',
  },
  accordionDetails: {
    position: 'relative',
    width: 'auto',
  },
  warning: {
    marginTop: 10,
    color: 'red',
  },
  tabs: {
    // marginBottom: 20,
  },
  tab: {
    color: COLOR.BLACK,
  },
  selectedTab: {
    backgroundColor: COLOR.LIGHT_BLUE,
    color: COLOR.WHITE,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  checkboxRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkboxText: {
    maxWidth: '80%',
  },
  subtitle: {
    fontFamily: 'FordAntenna-SemiBold',
    marginBottom: 5,
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 10,
    marginBottom: 10,
  },
  switchRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  switch: {
    '&>.MuiSwitch-colorPrimary': {
      color: '#00BDF2',
    },
    '&>.MuiSwitch-colorPrimary.Mui-checked': {
      color: 'white',
      backgroundColor: '#00BDF2',
    },
    '&>.MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
      color: 'white',
      backgroundColor: '#00BDF2',
    },
    '&>.MuiSwitch-colorSecondary.Mui-checked': {
      color: 'white',
      backgroundColor: '#00BDF2',
    },
    '&>.MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      opacity: 1,
    },
    '&>.MuiSwitch-track': {
      backgroundColor: '#00BDF2',
    },
    marginRight: 5,
    marginLeft: 5,
  },
  root: {
    width: 46,
    height: 26,
    padding: 0,
  },
  switchBase: {
    padding: 1,
    '&$checked': {
      transform: 'translateX(16px)',
      color: 'white',
      '& + $track': {
        opacity: 1,
        border: '6px solid #fff',
      },
    },
    '&$focusVisible $thumb': {
      border: '6px solid #fff',
    },
  },
  thumb: {
    width: 24,
    height: 24,
  },
  track: {
    borderRadius: 26 / 2,
    opacity: 1,
  },
});

function FloorplanModal() {
  const intl = useIntl();
  const styles = useStyles();
  const dispatch = useDispatch();
  const language = useLanguage();
  const { token, user } = useAuth();
  const { displayAlert } = useAlert();
  const [load, setLoad] = useState(false);
  const path = window.location.pathname;
  const arr = path.split('/');
  const distributionToken = arr[arr.length - 1];
  const [tabValue, setTabValue] = useState(0);
  const [is2DBiggerArea, set2DBiggerArea] = useState(false);
  const { listing, floorplanOrders, addedFloorplanOrderStatus } = useListing(distributionToken);
  const [option, setOption] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(floorplanOrders ? floorplanOrders[0] : 0);
  const [currentOrderExpanded, setCurrentOrderExpanded] = useState(false);
  const [floorplanOrder, setFloorplanOrder] = useState<FloorplanOrder>({
    id: null,
    listing_id: listing.id,
    url: path,
    user_id: user.id,
    created_at: new Date(),
    shooting_id: listing.validated_shooting_id,
    service_offer_id: null,
    property_type: listing.property_type,
  });
  const [showNewFloorplanOrder, setShowNewFloorplanOrder] = useState(!floorplanOrders.length);
  const [step, setStep] = useState(0);
  const isEn = language === ENG;
  const twoDFloorplans = [];
  const threeDFloorplans = [];
  const standardThreeDFloorplans = [];
  const advancedThreeDFloorplans = [];
  const [twoDFloorplanOptions, set2DFloorplanOptions] = useState([]);
  const [threeDFloorplanOptions, set3DFloorplanOptions] = useState([]);
  const [twoDSmallFloorplan, setTwoDSmallFloorplan] = useState(null);
  const [twoDBigFloorplan, setTwoDBigFloorplan] = useState(null);
  const [twoDFloorplanOption, set2DFloorplanOption] = useState(null);
  const [threeDFloorplanOption, set3DFloorplanOption] = useState(null);
  const [is3DWithFurnitures, set3DWithFurnitures] = useState(false);
  const [is3DBiggerArea, set3DBiggerArea] = useState(false);
  const fmtLargerThan200 = intl.formatMessage({
    id: 'deliver.photos.floorplanModal.largerThan200',
  });
  const fmtPleaseSubscribe = intl.formatMessage({
    id: 'deliver.photos.floorplanModal.pleaseSubscribe',
  });
  const fmtWithRealFurnitures = intl.formatMessage({
    id: 'deliver.photos.floorplanModal.withRealFurnitures',
  });
  const fmtSubtitle = intl.formatMessage({ id: 'deliver.photos.floorplanModal.subtitle' });
  const fmtSubmit = intl.formatMessage({ id: 'deliver.photos.submit' });
  const fmtNext = intl.formatMessage({ id: 'deliver.photos.next' });
  const fmtPrevious = intl.formatMessage({ id: 'deliver.photos.back' });
  const fmtCancel = intl.formatMessage({ id: 'deliver.photos.cancel' });
  const fmtOrder = intl.formatMessage({
    id: 'deliver.photos.virtualServiceModal.order',
  });
  const fmtDate = intl.formatMessage({ id: 'deliver.photos.virtualServiceModal.date' });
  const fmtFloorplans = intl.formatMessage({
    id: 'deliver.photos.virtualServiceModal.floorplans',
  });
  const fmtPrice = intl.formatMessage({
    id: 'deliver.photos.virtualServiceModal.price',
  });
  const fmtYourChoice = intl.formatMessage({
    id: 'deliver.photos.virtualServiceModal.yourChoice',
  });
  const fmtTaxes = intl.formatMessage({
    id: 'deliver.photos.virtualServiceModal.taxes',
  });
  const fmtTotal = intl.formatMessage({
    id: 'deliver.photos.virtualServiceModal.total',
  });
  const fmtCreateNewOrder = intl.formatMessage({
    id: 'deliver.photos.virtualServiceModal.createNewOrder',
  });
  const fmtModalTitle = intl.formatMessage({ id: 'deliver.photos.floorplan' });
  const fmtMeasurementsIncluded = intl.formatMessage({
    id: 'deliver.photos.floorplanModal.measurementsIncluded',
  });

  useEffect(() => {
    if (addedFloorplanOrderStatus) {
      setLoad(false);
      if (addedFloorplanOrderStatus === 200) {
        displayAlert(ALERT.SUCCESS, intl.formatMessage({ id: 'deliver.photos.success' }));
        dispatch(fetchListingAsync(distributionToken));
      } else {
        displayAlert(ALERT.ERROR, intl.formatMessage({ id: 'deliver.photos.error' }));
      }
      dispatch(hideModal(MODAL.FLOORPLAN));
      dispatch(getFloorplanOrdersAsync(token, listing.id, user.id));
    }
  }, [addedFloorplanOrderStatus]);

  useEffect(() => {
    if (listing.floorplanServices) {
      let twoDSmallFloorplan = null;
      let twoDBigFloorplan = null;
      let threeDSmallFloorplan = null;
      for (let i = 0; i < listing.floorplanServices.length; i++) {
        const serviceOffer = listing.floorplanServices[i];

        if (!serviceOffer.service_items) continue;

        const { type, withRealFurniture, isBiggerArea } = serviceOffer.service_items;

        if (type === '3D') {
          threeDFloorplans.push(serviceOffer);

          if (withRealFurniture) {
            advancedThreeDFloorplans.push(serviceOffer);
            continue;
          }

          standardThreeDFloorplans.push(serviceOffer);

          if (!isBiggerArea) {
            threeDSmallFloorplan = serviceOffer;
          }
          continue;
        }

        twoDFloorplans.push(serviceOffer);

        if (isBiggerArea) {
          twoDBigFloorplan = serviceOffer;
        } else {
          twoDSmallFloorplan = serviceOffer;
        }
      }

      set2DFloorplanOptions(twoDFloorplans);
      set3DFloorplanOptions(threeDFloorplans);
      setTwoDSmallFloorplan(twoDSmallFloorplan || null);
      setTwoDBigFloorplan(twoDBigFloorplan || null);
      set2DFloorplanOption(twoDSmallFloorplan || null);
      set3DFloorplanOption(threeDSmallFloorplan || null);
    }
  }, [listing]);

  const onClose = () => {
    dispatch(hideModal(MODAL.FLOORPLAN));
  };
  const onBack = () => {
    if (step) {
      setStep(0);
    } else {
      dispatch(hideModal(MODAL.FLOORPLAN));
    }
  };
  const onNext = () => {
    if (step) {
      setLoad(true);
      dispatch(
        addFloorplanOrderAsync(
          token,
          floorplanOrder,
          user.id,
          `${user.firstname} ${user.lastname}`,
          '3',
        ),
      );
    } else {
      setStep(1);
    }
  };
  const onCreateNewFloorplanOrder = () => {
    setShowNewFloorplanOrder(true);
  };
  const getTranslatedTitle = (so: any) => {
    if (!so) return '';
    if (isEn) {
      return so?.display_name;
    }
    return so?.display_name_fr;
  };
  const onChangeOption = (option: any) => {
    setOption(option);
    setFloorplanOrder({
      id: null,
      listing_id: listing.id,
      url: path,
      user_id: user.id,
      created_at: new Date(),
      shooting_id: listing.validated_shooting_id,
      service_offer_id: option?.id,
      property_type: listing.property_type,
    });
  };
  const onExpand = (id: number) => {
    setCurrentOrderId(id);
    if (currentOrderId === id) {
      setCurrentOrderExpanded(!currentOrderExpanded);
    } else {
      setCurrentOrderExpanded(true);
    }
  };
  const getOrderTaxes = (floorplanOrder: any) => {
    const price = option?.price || 0;
    return (price * 0.14975).toFixed(2);
  };
  const getOrderTotal = (floorplanOrder: any) => {
    const price = option?.price || 0;
    return (price * 1.14975).toFixed(2);
  };
  const getExampleImage = () => {
    let value;
    if (tabValue === 0) {
      value = twoDFloorplanOption;
    } else {
      value = _.find(threeDFloorplanOptions, (s) => {
        return (
          s.service_items.withRealFurniture === is3DWithFurnitures &&
          s.service_items.isBiggerArea === is3DBiggerArea
        );
      });
    }
    if (value) {
      if (value.service_items.type === '3D' && !value.service_items.withRealFurniture) {
        return images.standard_3d;
      }
      if (value.service_items.type === '3D' && value.service_items.withRealFurniture) {
        return images.advanced_3d;
      }
    }
    return images.floorplanExample;
  };
  const handleChange2D = (e: any) => {
    set2DBiggerArea(e.target.checked);
    if (e.target.checked) {
      set2DFloorplanOption(twoDBigFloorplan);
      onChangeOption(twoDBigFloorplan);
    } else {
      set2DFloorplanOption(twoDSmallFloorplan);
      onChangeOption(twoDSmallFloorplan);
    }
  };
  const render2DFloorplan = () => {
    return (
      <Box>
        <Box className={styles.checkboxRow}>
          <Typography>{fmtLargerThan200}</Typography>
          <Checkbox color='secondary' checked={is2DBiggerArea} onChange={handleChange2D} />
        </Box>
        {twoDFloorplanOptions.length ? (
          <Box className={styles.priceList}>
            <Box>
              <Typography>
                {isEn ? twoDFloorplanOption?.display_name : twoDFloorplanOption?.display_name_fr}
              </Typography>
              <Typography>{`${twoDFloorplanOption.price} $`}</Typography>
            </Box>
            {twoDFloorplanOption.id === option?.id ? (
              <CheckCircleIcon color='secondary' />
            ) : (
              <RadioButtonUncheckedOutlinedIcon
                onClick={() => {
                  return onChangeOption(twoDFloorplanOption);
                }}
              />
            )}
          </Box>
        ) : (
          <Typography className={styles.warning}>{fmtPleaseSubscribe}</Typography>
        )}
      </Box>
    );
  };
  const getCurrent3DService = (isWithFurnitures: boolean, isBiggerArea: boolean) => {
    const found3DService = _.find(threeDFloorplanOptions, (s) => {
      return (
        s.service_items.withRealFurniture === isWithFurnitures &&
        s.service_items.isBiggerArea === isBiggerArea
      );
    });
    if (found3DService) {
      set3DFloorplanOption(found3DService);
      onChangeOption(found3DService);
    }
  };

  const handle3DCheckbox = (e: any, type: string) => {
    switch (type) {
      case 'furnitures':
        set3DWithFurnitures(e.target.checked);
        getCurrent3DService(e.target.checked, is3DBiggerArea);
        break;
      case 'area':
        set3DBiggerArea(e.target.checked);
        getCurrent3DService(is3DWithFurnitures, e.target.checked);
        break;
      default:
        return null;
    }
    return null;
  };
  const render3DFloorplan = () => {
    return (
      <Box>
        {threeDFloorplanOptions.length ? (
          <Box>
            <Box>
              <Box className={styles.checkboxRow}>
                <Typography className={styles.checkboxText}>{fmtWithRealFurnitures}</Typography>
                <Checkbox
                  checked={is3DWithFurnitures}
                  onChange={(e) => {
                    return handle3DCheckbox(e, 'furnitures');
                  }}
                />
              </Box>
              <Box className={styles.checkboxRow}>
                <Typography className={styles.checkboxText}>{fmtLargerThan200}</Typography>
                <Checkbox
                  checked={is3DBiggerArea}
                  onChange={(e) => {
                    return handle3DCheckbox(e, 'area');
                  }}
                />
              </Box>
            </Box>
            <Box className={styles.priceList}>
              <Box>
                <Typography>
                  {isEn
                    ? threeDFloorplanOption?.display_name
                    : threeDFloorplanOption?.display_name_fr}
                </Typography>
                <Typography>{`${threeDFloorplanOption?.price} $`}</Typography>
              </Box>
              {threeDFloorplanOption?.id === option?.id ? (
                <CheckCircleIcon color='secondary' />
              ) : (
                <RadioButtonUncheckedOutlinedIcon
                  onClick={() => {
                    return onChangeOption(threeDFloorplanOption);
                  }}
                />
              )}
            </Box>
          </Box>
        ) : (
          <Typography className={styles.warning}>{fmtPleaseSubscribe}</Typography>
        )}
      </Box>
    );
  };
  const renderSummary = () => {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography>Service</Typography>
              </TableCell>
              <TableCell align='right'>
                <Typography>
                  {intl.formatMessage({
                    id: 'deliver.photos.virtualServiceModal.price',
                  })}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography>{isEn ? option?.display_name : option?.display_name_fr}</Typography>
              </TableCell>
              <TableCell align='right'>
                <Typography>{`$${option?.price}`}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography>
                  {intl.formatMessage({
                    id: 'deliver.photos.virtualServiceModal.subtotal',
                  })}
                </Typography>
              </TableCell>
              <TableCell align='right'>
                <Typography>{`$${option?.price}`}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography>
                  {intl.formatMessage({
                    id: 'deliver.photos.virtualServiceModal.taxes',
                  })}
                </Typography>
              </TableCell>
              <TableCell align='right'>
                <Typography>{`$${getOrderTaxes(option)}`}</Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography>
                  {intl.formatMessage({
                    id: 'deliver.photos.virtualServiceModal.total',
                  })}
                </Typography>
              </TableCell>
              <TableCell align='right'>
                <Typography>{`$${getOrderTotal(option)}`}</Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  const renderServiceOptions = () => {
    const handleTabChange = (event, newValue) => {
      setOption(null);
      setTabValue(newValue);
      set2DFloorplanOption(twoDSmallFloorplan);
      set2DBiggerArea(false);
    };
    const handleSwitchChange = (event) => {
      setOption(null);
      setTabValue(event.target.checked ? 1 : 0);
      set2DFloorplanOption(twoDSmallFloorplan);
      set2DBiggerArea(false);
      set3DBiggerArea(false);
      set3DWithFurnitures(false);
    };
    return (
      <Box>
        <Box>
          <img
            src={getExampleImage()}
            alt='floorplan'
            style={{ height: isMobile() ? 'auto' : 330, width: '100%', objectFit: 'contain' }}
          />
        </Box>
        <Box className={styles.switchRow}>
          <Typography>2D</Typography>
          <Switch
            className={styles.switch}
            checked={tabValue === 1}
            onChange={handleSwitchChange}
            classes={{
              root: styles.root,
              switchBase: styles.switchBase,
              thumb: styles.thumb,
              track: styles.track,
            }}
          />
          <Typography>3D</Typography>
        </Box>
        {tabValue === 0 ? render2DFloorplan() : render3DFloorplan()}
      </Box>
    );
  };
  const buttons = (
    <Box>
      <HvButton className={styles.cancelButton} onClick={onBack}>
        {step ? fmtPrevious : fmtCancel}
      </HvButton>
      <HvButton onClick={onNext} disabled={!listing.floorplanServices.length || !option?.id}>
        {step ? fmtSubmit : fmtNext}
      </HvButton>
    </Box>
  );

  const showNewOrder = (
    <Box className={styles.content}>
      <Typography className={styles.description}>{fmtSubtitle}</Typography>
      {renderServiceOptions()}
      {listing.broker.subscription_id === SUBSCRIPTION.carte ? (
        <Typography className={styles.explanation}>{fmtMeasurementsIncluded}</Typography>
      ) : null}
    </Box>
  );
  const showOrders = (
    <Box className={styles.content}>
      <Box className={styles.items}>
        {floorplanOrders &&
          floorplanOrders.map((floorplanOrder: any, index: number) => {
            return (
              <Accordion
                key={index}
                className={styles.accordion}
                expanded={currentOrderId === floorplanOrder.id && currentOrderExpanded}
                onChange={() => {
                  return onExpand(floorplanOrder.id);
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography className={styles.summary}>
                      {fmtOrder}
                      {floorplanOrder.id}
                    </Typography>
                    <Typography className={styles.summary}>
                      {fmtDate}
                      {moment.utc(floorplanOrder.created_at).local().format('YYYY-MM-DD HH:mm:ss')}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={styles.accordionDetails}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <Typography>{fmtFloorplans}</Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography>{fmtPrice}</Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <Box>
                              <Typography className={styles.optionTitle}>
                                {fmtYourChoice}
                              </Typography>
                              <Typography className={styles.optionSubtitle}>
                                {getTranslatedTitle(floorplanOrder?.service_offer)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography>{`$ ${floorplanOrder?.service_offer?.price}`}</Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Typography>{fmtTaxes}</Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography>{`$ ${getOrderTaxes(floorplanOrder)}`}</Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Typography>{fmtTotal}</Typography>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography>{`$${getOrderTotal(floorplanOrder)}`}</Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
      </Box>
      <HvButton className={styles.newOrderButton} onClick={onCreateNewFloorplanOrder}>
        <AddCircleIcon className={styles.addIcon} />
        {fmtCreateNewOrder}
      </HvButton>
    </Box>
  );
  const loadContent = () => {
    if (step === 1) {
      return renderSummary();
    }
    return showNewFloorplanOrder ? showNewOrder : showOrders;
  };
  return (
    <HvModal
      title={fmtModalTitle}
      headerImage='floorplanMesure'
      buttons={showNewFloorplanOrder ? buttons : null}
      onClose={onClose}
    >
      {loadContent()}
      {load ? <HvPleaseWait /> : null}
    </HvModal>
  );
}
export default FloorplanModal;
